import { ArrowUpRightIcon, HistoryIcon, InfoIcon, RefreshCwIcon } from "lucide-react";
import { Link } from "react-router";
import type { HistoryMatch } from "../../../../shared/api.ts";
import { Code } from "@/components/common/code.tsx";
import { MarkdownText } from "@/components/common/markdown.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useI18n } from "@/i18n/index.tsx";
import type { AssistantMessage, HistoryMatchMessage, NoticeMessage, ReuseMessage, UserMessage } from "@/lib/turns.ts";
import { cn } from "@/lib/utils.ts";
import { AttachmentChip } from "./attachment-chip.tsx";

/** The user's message: right-aligned bubble with the text and attachment chips. */
export function UserBubble({ message, own = true }: { message: UserMessage; own?: boolean }) {
  const { fmt, t } = useI18n();
  return (
    <div className="flex flex-col items-end gap-1.5">
      <div className="max-w-[min(85%,36rem)] rounded-2xl rounded-br-md border border-bubble-border bg-bubble px-4 py-2.5 text-[14px] leading-relaxed text-foreground">
        {message.text ? <p className="break-words whitespace-pre-wrap">{message.text}</p> : null}
        {message.attachments.length > 0 ? (
          <div className={cn("flex flex-wrap justify-end gap-1.5", message.text && "mt-2.5")}>
            {message.attachments.map((attachment) => (
              <AttachmentChip key={attachment.id} attachment={attachment} className="max-w-[16rem]" />
            ))}
          </div>
        ) : null}
      </div>
      <span className="pr-1 text-[11px] text-muted-foreground">
        {own ? `${t.messages.you} · ` : ""}
        {fmt.dateTime(message.createdAt)}
      </span>
    </div>
  );
}

/** An assistant message that has no turn in the session (should be rare). */
export function AssistantBubble({ message }: { message: AssistantMessage }) {
  return (
    <div className="rounded-xl border bg-card px-5 py-4">
      <MarkdownText text={message.text} />
    </div>
  );
}

function MatchRow({
  match,
  resolved,
  onOpen,
  onUse,
  busy,
}: {
  match: HistoryMatch;
  resolved: boolean;
  onOpen: (sessionId: string) => void;
  onUse: (match: HistoryMatch) => void;
  busy: boolean;
}) {
  const { t, fmt } = useI18n();
  const code = match.declaredCode ?? match.chosenCode;
  return (
    <li className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          {resolved ? (
            <button
              type="button"
              onClick={() => onOpen(match.sessionId)}
              className="truncate text-left text-[13.5px] font-semibold text-primary hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/35 focus-visible:outline-none"
            >
              {match.title}
            </button>
          ) : (
            <span className="truncate text-[13.5px] font-semibold">{match.title}</span>
          )}
          <Badge variant={match.reason === "article" ? "primary" : "neutral"}>
            {match.reason === "article" ? t.messages.reasonArticle : t.messages.reasonSimilar}
          </Badge>
        </div>
        {match.goodName && match.goodName !== match.title ? (
          <p className="mt-0.5 truncate text-[12.5px] text-muted-foreground">{match.goodName}</p>
        ) : null}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]">
          {match.chosenCode ? (
            <span className="inline-flex items-center gap-1.5">
              <Code value={match.chosenCode} className="font-semibold text-foreground" />
              <span className="text-muted-foreground">{t.messages.chosen}</span>
            </span>
          ) : null}
          {match.declaredCode ? (
            <span className="inline-flex items-center gap-1.5">
              <Code value={match.declaredCode} className="font-semibold text-foreground" />
              <Badge variant="success" className="px-1.5 py-0 text-[10.5px]">
                {t.messages.declared}
              </Badge>
            </span>
          ) : null}
          {!code ? <span className="text-muted-foreground">{t.messages.noCode}</span> : null}
          <span className="text-muted-foreground">{t.messages.classifiedOn(fmt.date(match.classifiedAt))}</span>
        </div>
      </div>
      {!resolved ? (
        <div className="flex shrink-0 gap-2">
          <Button variant="ghost" size="sm" onClick={() => onOpen(match.sessionId)}>
            <ArrowUpRightIcon aria-hidden />
            {t.messages.open}
          </Button>
          <Button variant="outline" size="sm" disabled={!code || busy} onClick={() => onUse(match)}>
            {t.messages.useCode}
          </Button>
        </div>
      ) : null}
    </li>
  );
}

/** "This good was classified before": open a match, reuse its code, or run a new analysis. */
export function HistoryMatchCard({
  message,
  readOnly,
  onOpen,
  onUse,
  onAnalyze,
  busy,
}: {
  message: HistoryMatchMessage;
  readOnly: boolean;
  onOpen: (sessionId: string) => void;
  onUse: (match: HistoryMatch, message: HistoryMatchMessage) => void;
  onAnalyze: (message: HistoryMatchMessage) => void;
  busy: boolean;
}) {
  const { t } = useI18n();
  const resolved = message.resolution !== null || readOnly;
  return (
    <section className="overflow-hidden rounded-xl border border-primary/25 bg-card shadow-[0_1px_3px_rgba(15,27,45,0.05)]">
      <header className="flex items-start gap-3 border-b bg-primary-soft/60 px-4 py-3">
        <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <HistoryIcon className="size-4" aria-hidden />
        </span>
        <div className="min-w-0">
          <h3 className="text-[14px] font-bold">{t.messages.historyTitle}</h3>
          <p className="mt-0.5 text-[12.5px] text-muted-foreground">{t.messages.historyText}</p>
        </div>
      </header>
      <ul className="divide-y">
        {message.matches.map((match) => (
          <MatchRow
            key={match.sessionId}
            match={match}
            resolved={resolved}
            busy={busy}
            onOpen={onOpen}
            onUse={(picked) => onUse(picked, message)}
          />
        ))}
      </ul>
      <footer className="flex flex-wrap items-center justify-between gap-2 border-t bg-muted/30 px-4 py-2.5">
        {message.resolution ? (
          <p className="text-[12.5px] font-medium text-muted-foreground">
            {message.resolution === "reused" ? t.messages.resolvedReused : t.messages.resolvedNew}
          </p>
        ) : (
          <span className="text-[12.5px] text-muted-foreground">{readOnly ? null : t.composer.awaitingChoice}</span>
        )}
        {!resolved ? (
          <Button size="sm" disabled={busy} onClick={() => onAnalyze(message)}>
            <RefreshCwIcon aria-hidden />
            {t.messages.newAnalysis}
          </Button>
        ) : null}
      </footer>
    </section>
  );
}

/** "Code 8482 40 00 00 was taken from session …" with a link to it. */
export function ReuseCard({ message, linkPrefix }: { message: ReuseMessage; linkPrefix: string }) {
  const { t } = useI18n();
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border bg-card px-4 py-3 text-[13px]">
      <HistoryIcon className="size-4 shrink-0 text-primary" aria-hidden />
      <span className="text-muted-foreground">{t.messages.reuseCode}</span>
      <span className="rounded-md border bg-muted/60 px-1.5 py-px">
        <Code value={message.code} className="text-[13px] font-semibold" />
      </span>
      <span className="text-muted-foreground">{t.messages.reusedFrom}</span>
      <Link to={`${linkPrefix}${message.fromSessionId}`} className="min-w-0 truncate font-semibold text-primary hover:underline">
        {message.text}
      </Link>
    </div>
  );
}

/** A small muted centered note. */
export function NoticeLine({ message }: { message: NoticeMessage }) {
  return (
    <p className="flex items-center justify-center gap-1.5 px-6 text-center text-[12.5px] text-muted-foreground">
      <InfoIcon className="size-3.5 shrink-0" aria-hidden />
      <span>{message.text}</span>
    </p>
  );
}
